// ☑️ B390 (AI_157) — QUADRO "CONDUTAS SUGERIDAS PELA ÚLTIMA ANÁLISE"
// Topo da aba Consultas, abaixo dos alertas do prontuário. Recolhido por
// padrão (cabeçalho com contador); aberto, lista os itens da seção
// "RECOMENDAÇÕES" da última análise geral, agrupados como lá, com
// Acatada / Descartar (só médico) e a dica "provável: receita de dd/mm"
// (cruzamento com receitas/pedidos POSTERIORES à análise — nunca apaga só).
// Estado gravado em analises-ia/{id}.condutas.<chave> (regras: médico pode
// atualizar mantendo a autoria — B142). Nova análise = lista nova.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  ClipboardList,
  ChevronDown,
  ChevronRight,
  Check,
  X,
  Undo2,
  ExternalLink,
} from "lucide-react";
import {
  collection,
  deleteField,
  doc,
  getDocs,
  orderBy,
  query,
  updateDoc,
  where,
} from "firebase/firestore";
import { db } from "../../../lib/firebase";
import { Paciente } from "../../../types";
import { theme } from "../../../shared/styles/theme";
import { useAuth } from "../../auth/context/AuthContext";
import { ehMedico } from "../../../shared/utils/permissoesCategoria";
import { useIAAnaliseContext } from "../context/IAAnaliseContext";
import { AnaliseIA } from "../types";
import {
  extrairCondutas,
  detectarAcatamentoProvavel,
  resumirCondutas,
  EstadoConduta,
  FonteReceita,
  FontePedido,
  ItemConduta,
} from "../services/condutasSugeridas";

const SINAL: Record<string, string> = {
  verde: "🟢",
  amarelo: "🟡",
  vermelho: "🔴",
};

function fmtDataHora(iso: string): string {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  return d.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" });
}

export function QuadroCondutasSugeridas({
  paciente,
  onVerAnalise,
}: {
  paciente: Paciente;
  onVerAnalise?: () => void;
}) {
  const { usuarioLogado, firebaseUser } = useAuth();
  const { analiseAtual, buscarUltimaAnalise } = useIAAnaliseContext();
  const podeMarcar = ehMedico(usuarioLogado) && !!firebaseUser;

  const [analise, setAnalise] = useState<AnaliseIA | null>(null);
  const [aberto, setAberto] = useState(false);
  const [fontes, setFontes] = useState<{ receitas: FonteReceita[]; pedidos: FontePedido[] }>({
    receitas: [],
    pedidos: [],
  });
  const [salvando, setSalvando] = useState<string | null>(null);
  const [erro, setErro] = useState<string | null>(null);

  // Última análise do paciente (recarrega quando uma nova é gerada). A função
  // do contexto não é memoizada → vai por ref, para não re-disparar a cada render
  // (e sem desligar o lint do CI, que não conhece a regra react-hooks).
  const buscarRef = useRef(buscarUltimaAnalise);
  buscarRef.current = buscarUltimaAnalise;
  useEffect(() => {
    let vivo = true;
    buscarRef.current(paciente.id).then((a) => {
      if (vivo) setAnalise(a);
    });
    return () => {
      vivo = false;
    };
  }, [paciente.id, analiseAtual?.id]);

  // Receitas e pedidos do paciente, para a dica "provável já prescrito".
  // Só ao abrir o quadro (não custa leitura para quem nunca abre).
  useEffect(() => {
    if (!aberto) return;
    let vivo = true;
    (async () => {
      try {
        const snap = await getDocs(
          query(
            collection(db, "prescricoes"),
            where("pacienteId", "==", paciente.id),
            orderBy("criadoEm", "desc")
          )
        );
        const receitas: FonteReceita[] = [];
        const pedidos: FontePedido[] = [];
        snap.docs.forEach((d) => {
          const p: any = d.data();
          const data = p.criadoEm?.toDate?.()?.toISOString?.() || p.criadoEm || "";
          const c = p.conteudo || {};
          if (String(p.tipo || "").startsWith("receituario")) {
            const meds = (c.medicamentos || [])
              .map((m: any) => (typeof m === "string" ? m : m?.nome || ""))
              .filter(Boolean);
            if (meds.length) receitas.push({ data, medicamentos: meds });
          } else if (p.tipo === "exames") {
            const ex = (c.exames || [])
              .map((e: any) => (typeof e === "string" ? e : e?.nome || ""))
              .filter(Boolean);
            if (ex.length) pedidos.push({ data, exames: ex });
          }
        });
        if (vivo) setFontes({ receitas, pedidos });
      } catch (e) {
        console.warn("⚠️ [B390] não carregou receitas/pedidos para a dica:", e);
      }
    })();
    return () => {
      vivo = false;
    };
  }, [aberto, paciente.id]);

  const texto = analise?.resumo?.principaisAchados?.[0] || "";
  const grupos = useMemo(() => extrairCondutas(texto), [texto]);
  const estados = analise?.condutas || {};
  const resumo = useMemo(() => resumirCondutas(grupos, estados), [grupos, estados]);

  const gravar = useCallback(
    async (item: ItemConduta, estado: EstadoConduta | null) => {
      if (!analise?.id || !podeMarcar) return;
      setSalvando(item.chave);
      setErro(null);
      try {
        await updateDoc(doc(db, "analises-ia", analise.id), {
          [`condutas.${item.chave}`]: estado ?? deleteField(),
        });
        setAnalise((prev) => {
          if (!prev) return prev;
          const condutas = { ...(prev.condutas || {}) };
          if (estado) condutas[item.chave] = estado;
          else delete condutas[item.chave];
          return { ...prev, condutas };
        });
      } catch (e: any) {
        console.error("❌ [B390] falha ao gravar conduta:", e);
        setErro("Não foi possível gravar a marcação. Tente de novo.");
      } finally {
        setSalvando(null);
      }
    },
    [analise?.id, podeMarcar]
  );

  const marcar = (item: ItemConduta, status: "acatada" | "descartada") =>
    gravar(item, {
      status,
      em: new Date().toISOString(),
      porUid: firebaseUser!.uid,
      porEmail: firebaseUser!.email ?? null,
    });

  if (!analise) return null;

  const dataAnalise = fmtDataHora(analise.dataAnalise);
  const semSecao = grupos.length === 0;

  return (
    <div
      style={{
        marginBottom: "20px",
        border: `1px solid ${theme.primary[200]}`,
        borderRadius: theme.borderRadius.lg,
        background: "white",
        overflow: "hidden",
        fontFamily: theme.fonts.sans,
      }}
    >
      <button
        onClick={() => setAberto((v) => !v)}
        style={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          gap: "10px",
          padding: "12px 16px",
          background: theme.primary[50],
          border: "none",
          cursor: "pointer",
          fontFamily: theme.fonts.sans,
          fontSize: "14px",
          color: theme.primary[800],
          textAlign: "left",
        }}
      >
        {aberto ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
        <ClipboardList size={16} color={theme.primary[600]} />
        <span style={{ fontWeight: 700 }}>
          Condutas sugeridas pela análise de {dataAnalise}
        </span>
        <span style={{ fontSize: "12px", color: theme.gray[600], marginLeft: "auto" }}>
          {semSecao
            ? "sem seção de recomendações reconhecível"
            : `${resumo.pendentes} pendente${resumo.pendentes === 1 ? "" : "s"} · ${
                resumo.acatadas
              } acatada${resumo.acatadas === 1 ? "" : "s"} · ${resumo.descartadas} descartada${
                resumo.descartadas === 1 ? "" : "s"
              }`}
        </span>
      </button>

      {aberto && (
        <div style={{ padding: "12px 16px 16px" }}>
          {semSecao ? (
            <div style={{ fontSize: "13px", color: theme.gray[600] }}>
              A análise de {dataAnalise} não trouxe uma seção de recomendações que o
              sistema reconheça. Abra a análise completa para ler as condutas.
            </div>
          ) : (
            grupos.map((g) => (
              <div key={g.titulo} style={{ marginBottom: "12px" }}>
                <div
                  style={{
                    fontSize: "12px",
                    fontWeight: 700,
                    color: theme.gray[700],
                    textTransform: "uppercase",
                    letterSpacing: "0.03em",
                    margin: "6px 0",
                  }}
                >
                  {g.titulo}
                </div>
                {g.itens.map((item) => {
                  const estado = estados[item.chave];
                  const dica =
                    !estado && analise.dataAnalise
                      ? detectarAcatamentoProvavel(item, fontes, analise.dataAnalise)
                      : null;
                  const apagado = !!estado;
                  return (
                    <div
                      key={item.chave}
                      style={{
                        display: "flex",
                        gap: "10px",
                        alignItems: "flex-start",
                        padding: "8px 10px",
                        borderRadius: theme.borderRadius.md,
                        background: apagado ? theme.gray[50] : "white",
                        border: `1px solid ${
                          estado?.status === "acatada"
                            ? theme.status.success.border
                            : estado?.status === "descartada"
                            ? theme.gray[300]
                            : theme.gray[200]
                        }`,
                        marginBottom: "6px",
                        opacity: apagado ? 0.7 : 1,
                      }}
                    >
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div
                          style={{
                            fontSize: "13px",
                            color: theme.gray[800],
                            lineHeight: 1.45,
                            textDecoration:
                              estado?.status === "descartada" ? "line-through" : "none",
                          }}
                        >
                          {item.sinal ? SINAL[item.sinal] + " " : ""}
                          {item.texto}
                        </div>
                        {item.detalhes.length > 0 && !apagado && (
                          <ul
                            style={{
                              margin: "4px 0 0 18px",
                              padding: 0,
                              fontSize: "12px",
                              color: theme.gray[600],
                              lineHeight: 1.4,
                            }}
                          >
                            {item.detalhes.map((d, i) => (
                              <li key={i}>{d}</li>
                            ))}
                          </ul>
                        )}
                        {estado && (
                          <div style={{ fontSize: "11px", color: theme.gray[500], marginTop: "3px" }}>
                            {estado.status === "acatada" ? "Acatada" : "Descartada"} em{" "}
                            {fmtDataHora(estado.em)}
                            {estado.porEmail ? ` · ${estado.porEmail}` : ""}
                          </div>
                        )}
                        {dica && (
                          <div
                            style={{
                              fontSize: "11px",
                              color: theme.status.success.text,
                              background: theme.status.success.bg,
                              display: "inline-block",
                              padding: "2px 8px",
                              borderRadius: "10px",
                              marginTop: "4px",
                            }}
                          >
                            provável: {dica} — confirme
                          </div>
                        )}
                      </div>
                      {podeMarcar && (
                        <div style={{ display: "flex", gap: "4px", flexShrink: 0 }}>
                          {!estado ? (
                            <>
                              <BotaoAcao
                                titulo="Acatada"
                                cor={theme.status.success.border}
                                ocupado={salvando === item.chave}
                                onClick={() => marcar(item, "acatada")}
                              >
                                <Check size={14} />
                              </BotaoAcao>
                              <BotaoAcao
                                titulo="Descartar"
                                cor={theme.gray[500]}
                                ocupado={salvando === item.chave}
                                onClick={() => marcar(item, "descartada")}
                              >
                                <X size={14} />
                              </BotaoAcao>
                            </>
                          ) : (
                            <BotaoAcao
                              titulo="Desfazer"
                              cor={theme.gray[500]}
                              ocupado={salvando === item.chave}
                              onClick={() => gravar(item, null)}
                            >
                              <Undo2 size={14} />
                            </BotaoAcao>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ))
          )}
          {erro && (
            <div style={{ fontSize: "12px", color: theme.status.error.text, marginTop: "6px" }}>
              {erro}
            </div>
          )}
          {onVerAnalise && (
            <button
              onClick={onVerAnalise}
              style={{
                marginTop: "4px",
                display: "inline-flex",
                alignItems: "center",
                gap: "6px",
                background: "transparent",
                border: "none",
                cursor: "pointer",
                fontFamily: theme.fonts.sans,
                fontSize: "12px",
                color: theme.primary[700],
                padding: 0,
              }}
            >
              <ExternalLink size={12} /> ver análise completa
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function BotaoAcao({
  titulo,
  cor,
  ocupado,
  onClick,
  children,
}: {
  titulo: string;
  cor: string;
  ocupado: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      title={titulo}
      aria-label={titulo}
      disabled={ocupado}
      onClick={onClick}
      style={{
        width: "28px",
        height: "28px",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        border: `1px solid ${cor}`,
        color: cor,
        background: "white",
        borderRadius: "6px",
        cursor: ocupado ? "wait" : "pointer",
        opacity: ocupado ? 0.5 : 1,
      }}
    >
      {children}
    </button>
  );
}
